import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { X } from 'lucide-react';
import { fmt12 } from './TimeField';

// ─── Time entry for events ───────────────────────────────────────────────────
// The answer is nearly always one of three sailing times, so those are
// offered as one-tap chips for the chosen date (SailingChips, from
// cruise_details_on() so a scheduled change is respected). Free entry stays
// for everything else: type "5pm", "17:00", "4.30 pm" or "0600" and it is
// read on blur or Enter. Storage is always 24-hour HH:MM text, like TimeField.

const pad2 = (n) => String(n).padStart(2, '0');

/** Free text → 'HH:MM', '' when empty, null when it cannot be read. */
export function parseTimeText(raw) {
  const s = String(raw || '').trim().toLowerCase().replace(/\s+/g, ' ');
  if (!s) return '';
  const merMatch = /([ap])\.?\s?m?\.?$/.exec(s);
  const mer = merMatch ? merMatch[1] : null;
  const core = mer ? s.slice(0, merMatch.index).trim() : s;
  let h, mi;
  let m = /^(\d{1,2})[:.](\d{2})$/.exec(core);
  if (m) { h = Number(m[1]); mi = Number(m[2]); }
  else if (/^\d{3,4}$/.test(core)) { h = Number(core.slice(0, core.length - 2)); mi = Number(core.slice(-2)); }
  else if (/^\d{1,2}$/.test(core)) { h = Number(core); mi = 0; }
  else return null;
  if (mi > 59) return null;
  if (mer) {
    if (h < 1 || h > 12) return null;
    if (mer === 'p' && h < 12) h += 12;
    if (mer === 'a' && h === 12) h = 0;
  } else if (h > 23) return null;
  return `${pad2(h)}:${pad2(mi)}`;
}

export function TimeEntry({ id, value, onChange, placeholder = 'e.g. 5:00 PM' }) {
  const [text, setText] = useState(value ? fmt12(value) : '');
  const [bad, setBad] = useState(false);
  // The stored value changed from outside (a sailing chip, the clear button,
  // a loaded event): show it and drop any unreadable text left in the box.
  useEffect(() => { setText(value ? fmt12(value) : ''); setBad(false); }, [value]);

  const commit = () => {
    const parsed = parseTimeText(text);
    if (parsed === null) { setBad(true); return; }
    setBad(false);
    onChange(parsed);
    setText(parsed ? fmt12(parsed) : '');
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 6, alignItems: 'stretch' }}>
        <input id={id} type="text" value={text} placeholder={placeholder} autoComplete="off"
               onChange={e => { setText(e.target.value); if (bad) setBad(false); }}
               onBlur={commit}
               onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); commit(); } }}
               aria-invalid={bad || undefined}
               style={{ flex: 1, minWidth: 0, borderColor: bad ? 'var(--color-danger)' : undefined }} />
        {value && (
          <button type="button" onClick={() => { onChange(''); setText(''); setBad(false); }} aria-label="Clear time" className="btn btn-ghost" style={{ padding: '0 10px' }}>
            <X size={15} strokeWidth={2} aria-hidden="true" />
          </button>
        )}
      </div>
      {bad && <span className="field-error">Could not read that time. Try 5:00 PM or 17:00.</span>}
    </div>
  );
}

// The sailings in force on a date, one RPC per date, cached for the session.
const sailingsCache = new Map();
async function sailingsOn(iso) {
  if (sailingsCache.has(iso)) return sailingsCache.get(iso);
  const { data, error } = await supabase.rpc('cruise_details_on', { p_date: iso });
  const rows = error || !Array.isArray(data) ? [] : data
    .filter(r => r.departure_time && r.end_time && r.is_active !== false)
    .sort((a, b) => String(a.departure_time).localeCompare(String(b.departure_time)));
  if (!error) sailingsCache.set(iso, rows);
  return rows;
}

export function SailingChips({ date, start, end, onPick }) {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    let cancelled = false;
    if (!date || !/^\d{4}-\d{2}-\d{2}$/.test(date)) { setRows([]); return undefined; }
    sailingsOn(date).then(r => { if (!cancelled) setRows(r); });
    return () => { cancelled = true; };
  }, [date]);
  if (!rows.length) return null;
  const hhmm = (t) => String(t).slice(0, 5);
  return (
    <div className="ev-sailing-chips" role="group" aria-label="Sailing times on this date">
      {rows.map(r => {
        const dep = hhmm(r.departure_time), fin = hhmm(r.end_time);
        const on = start === dep && end === fin;
        return (
          <button key={r.cruise_name} type="button" className="ev-chip-toggle" aria-pressed={on} onClick={() => onPick(dep, fin)}
                  style={{ background: on ? 'var(--color-accent-ring)' : 'var(--color-surface)', borderColor: on ? 'var(--color-accent)' : 'var(--color-border-strong)', color: on ? 'var(--color-accent-strong)' : 'var(--color-ink-brand)' }}>
            {r.cruise_name} <span style={{ fontWeight: 500, color: 'var(--color-ink-muted)' }}>{fmt12(dep)} to {fmt12(fin)}</span>
          </button>
        );
      })}
    </div>
  );
}
